import { useCreateContextHook } from 'contexts/useCreateContextHook';
import React, {
    createContext,
    useCallback,
    useRef,
    useState,
    useEffect,
} from 'react';
import { Classifier } from 'types/generated/graphql';
import { neverop } from 'utils';

export type ClassifiersContextType = {
    hiddenCount: number;
    hiddenClassifiers: Classifier[];
    registerPill: (
        pillId: string,
        ref: React.RefObject<HTMLDivElement>,
        classifier: Classifier,
    ) => void;
    isClassifierHidden: (classifierId: string) => boolean;
};

export const defaultClassifiersContextContext: ClassifiersContextType = {
    hiddenCount: 0,
    hiddenClassifiers: [],
    registerPill: neverop('SuggestionsPillsContext', 'registerPill'),
    isClassifierHidden: neverop(
        'ClassifiersContext',
        'isClassifierHidden',
        false,
    ),
};

export const ClassifiersContext = createContext<ClassifiersContextType>(
    defaultClassifiersContextContext,
);

const useClassifiersProvider = ({
    containerRef,
}: {
    containerRef: React.RefObject<HTMLDivElement>;
}) => {
    const ClassifiersRefs = useRef<
        Map<
            string,
            { ref: React.RefObject<HTMLDivElement>; classifier: Classifier }
        >
    >(new Map());

    // Track which classifiers have already been counted as hidden
    const hiddenClassifiersSet = useRef<Set<HTMLDivElement>>(new Set());

    const [hiddenClassifiers, setHiddenClassifiers] = useState<Classifier[]>(
        [],
    );

    // Check all classifiers for overflow
    const checkAllClassifiersOverflow = useCallback(() => {
        const overflowElement = containerRef.current;

        if (!overflowElement) {
            setHiddenClassifiers([]);
            hiddenClassifiersSet.current.clear();
            return;
        }

        const overflowRect = overflowElement.getBoundingClientRect();
        const newHiddenClassifiers: Classifier[] = [];
        const newHiddenSet = new Set<HTMLDivElement>();

        // Check each registered classifier
        ClassifiersRefs.current.forEach(({ ref, classifier }, classifierId) => {
            const classifierElement = ref.current;

            if (!classifierElement) return;

            const classifierRect = classifierElement.getBoundingClientRect();

            // Check if the classifier is overflowing the container
            // A classifier is overflowing if its right edge (plus some padding) exceeds the container's right edge
            const isOverflowing =
                classifierRect.right + 20 > overflowRect.right;

            if (isOverflowing) {
                newHiddenSet.add(classifierElement);
                newHiddenClassifiers.push(classifier);
            }
        });

        setHiddenClassifiers(newHiddenClassifiers);
        hiddenClassifiersSet.current = newHiddenSet;
    }, [containerRef]);

    const registerPill = useCallback(
        (
            classifierId: string,
            ref: React.RefObject<HTMLDivElement>,
            classifier: Classifier,
        ) => {
            ClassifiersRefs.current.set(classifierId, { ref, classifier });
        },
        [],
    );

    const isClassifierHidden = useCallback((classifierId: string) => {
        const classifierData = ClassifiersRefs.current.get(classifierId);

        if (!classifierData) return false;

        return hiddenClassifiersSet.current.has(classifierData.ref.current!);
    }, []);

    // Set up ResizeObserver to check all classifiers when container changes
    useEffect(() => {
        const containerElement = containerRef.current;

        const resizeObserver = new ResizeObserver(() => {
            checkAllClassifiersOverflow();
        });

        if (containerElement) {
            resizeObserver.observe(containerElement);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [checkAllClassifiersOverflow, containerRef]);

    return {
        hiddenCount: hiddenClassifiers.length,
        hiddenClassifiers,
        registerPill,
        isClassifierHidden,
    };
};

export const ClassifiersProvider: React.FC<{
    containerRef: React.RefObject<HTMLDivElement>;
    children: React.ReactNode;
}> = ({ children, containerRef }) => {
    const { hiddenCount, hiddenClassifiers, registerPill, isClassifierHidden } =
        useClassifiersProvider({ containerRef });

    return (
        <ClassifiersContext.Provider
            value={{
                hiddenCount,
                hiddenClassifiers,
                registerPill,
                isClassifierHidden,
            }}
        >
            {children}
        </ClassifiersContext.Provider>
    );
};

export const useClassifiersContext = () => {
    const context = useCreateContextHook({
        context: ClassifiersContext,
        errorMessage:
            'useClassifiersContext must be used within a ClassifiersContext',
    });

    return context;
};

export default ClassifiersProvider;
